import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material"
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { DataType, DeviceDataGroup } from "graphql/types"
import { useCallback, useEffect, useRef, useState } from "react"
import { normalizeNumberInput } from "utils/numberInputHelpers"
import { CardActionButton } from "./CardActionButton"

type EUTDataCollapsibleProps = {
  group: DeviceDataGroup
  onSave: (arg: DeviceDataGroup) => void
  isSaveDisabled: boolean
  isCardActions?: boolean
  cardActions?: React.ReactNode
}

export const EUTDataCollapsible = (props: EUTDataCollapsibleProps) => {
  const { group: initialGroup, onSave, isSaveDisabled, isCardActions = false, cardActions } = props
  const [editableGroup, setEditableGroup] = useState<DeviceDataGroup>(initialGroup)
  const [isEditMode, setIsEditMode] = useState(false)
  const [floatInputs, setFloatInputs] = useState<Record<string, string>>({})
  const editingFloatRef = useRef(false)

  const updateFloatInputs = useCallback(
    (updater: (prev: Record<string, string>) => Record<string, string>) => {
      setFloatInputs((prev) => {
        const next = updater(prev)
        editingFloatRef.current = Object.keys(next).length > 0
        return next
      })
    },
    []
  )

  useEffect(() => {
    if (editingFloatRef.current && initialGroup.id === editableGroup.id) {
      return
    }
    setEditableGroup(JSON.parse(JSON.stringify(initialGroup)))
    updateFloatInputs(() => ({}))
  }, [initialGroup, editableGroup.id, updateFloatInputs])

  // Helper function to format numbers for German display
  const formatNumberForDisplay = (value: any): string => {
    if (value === "" || value === null || value === undefined) return ""
    if (typeof value === "string" || typeof value === "number") {
      return value.toString().replace(/\./g, ",")
    }
    return String(value)
  }

  const floatInputPattern = /^-?[0-9]*([,.][0-9]*)?$/

  const prepareGroupForSave = (group: DeviceDataGroup): DeviceDataGroup => {
    const clone = JSON.parse(JSON.stringify(group)) as DeviceDataGroup
    clone.deviceData = clone.deviceData.map((item) => {
      if (item.dataType !== DataType.FLOAT) {
        return item
      }
      const dataEntries = (item.data || []).map((entry) => {
        if (typeof entry.value === "string") {
          const trimmed = entry.value.trim()
          if (trimmed === "") {
            return { ...entry, value: "" }
          }
          if (floatInputPattern.test(trimmed)) {
            return { ...entry, value: normalizeNumberInput(trimmed) }
          }
        }
        return entry
      })
      return { ...item, data: dataEntries }
    })
    return clone
  }

  const getFloatFieldKey = (item: DeviceDataGroup["deviceData"][number], dataIdx?: number) => {
    const baseKey = item.id || item.name
    return typeof dataIdx === "number" ? `${baseKey}-${dataIdx}` : baseKey
  }

  const getFloatDisplayValue = (
    item: DeviceDataGroup["deviceData"][number],
    value: any,
    dataIdx?: number
  ) => {
    if (item.dataType !== DataType.FLOAT) {
      return value
    }
    const key = getFloatFieldKey(item, dataIdx)
    return floatInputs[key] ?? formatNumberForDisplay(value)
  }

  const handleInputChange = (
    itemIndex: number,
    value: string | number | boolean,
    field: "value" | "unit",
    dataIndex?: number
  ) => {
    const newEditableGroup = JSON.parse(JSON.stringify(editableGroup)) as DeviceDataGroup
    const deviceDataItem = newEditableGroup.deviceData[itemIndex]

    let parsedValue = value
    if (field === "value") {
      if (deviceDataItem.dataType === DataType.FLOAT) {
        if (typeof value === "string") {
          const key = getFloatFieldKey(deviceDataItem, dataIndex)
          updateFloatInputs((prev) => ({ ...prev, [key]: value }))

          const trimmedValue = value.trim()
          if (trimmedValue === "") {
            parsedValue = ""
          } else if (floatInputPattern.test(trimmedValue)) {
            parsedValue = normalizeNumberInput(trimmedValue)
          } else {
            // Keep previous value if input is invalid, but retain display string
            parsedValue =
              deviceDataItem.isArray && typeof dataIndex === "number"
                ? (deviceDataItem.data?.[dataIndex]?.value ?? "")
                : (deviceDataItem.data?.[0]?.value ?? "")
          }
        } else {
          parsedValue = value
        }
      } else if (deviceDataItem.dataType === DataType.BOOLEAN) {
        parsedValue = value as boolean
      }
    }

    if (deviceDataItem.isArray && typeof dataIndex === "number") {
      if (!deviceDataItem.data) deviceDataItem.data = []
      while (deviceDataItem.data.length <= dataIndex) {
        deviceDataItem.data.push({
          value: deviceDataItem.dataType === DataType.BOOLEAN ? false : "",
          unit: deviceDataItem.unit.length > 0 ? deviceDataItem.unit[0] : "",
          note: "",
        })
      }

      if (field === "value") {
        deviceDataItem.data[dataIndex].value = parsedValue
      } else {
        deviceDataItem.data[dataIndex].unit = value as string
      }
    } else {
      if (!deviceDataItem.data || deviceDataItem.data.length === 0) {
        deviceDataItem.data = [
          {
            value: deviceDataItem.dataType === DataType.BOOLEAN ? false : "",
            unit: deviceDataItem.unit.length > 0 ? deviceDataItem.unit[0] : "",
            note: "",
          },
        ]
      }
      if (field === "value") {
        deviceDataItem.data[0].value = parsedValue
      } else {
        deviceDataItem.data[0].unit = value as string
      }
    }
    setEditableGroup(newEditableGroup)

    // Only auto-save if not in card actions mode
    if (!isCardActions) {
      const prepared = prepareGroupForSave(newEditableGroup)

      onSave(prepared)
    }
  }

  const addArrayItem = (itemIndex: number) => {
    const newEditableGroup = JSON.parse(JSON.stringify(editableGroup)) as DeviceDataGroup
    const deviceDataItem = newEditableGroup.deviceData[itemIndex]
    if (!deviceDataItem.data) {
      deviceDataItem.data = []
    }
    const defaultUnit = deviceDataItem.unit.length > 0 ? deviceDataItem.unit[0] : ""
    deviceDataItem.data.push({ value: "", unit: defaultUnit, note: "" })
    setEditableGroup(newEditableGroup)

    // Only auto-save if not in card actions mode
    if (!isCardActions) {
      onSave(prepareGroupForSave(newEditableGroup))
    }
  }

  const removeArrayItem = (itemIndex: number, dataIndex: number) => {
    const newEditableGroup = JSON.parse(JSON.stringify(editableGroup)) as DeviceDataGroup
    const deviceDataItem = newEditableGroup.deviceData[itemIndex]
    if (deviceDataItem.data && deviceDataItem.data.length > dataIndex) {
      deviceDataItem.data.splice(dataIndex, 1)
      setEditableGroup(newEditableGroup)
      if (deviceDataItem.dataType === DataType.FLOAT) {
        const key = getFloatFieldKey(deviceDataItem, dataIndex)
        updateFloatInputs((prev) => {
          const { [key]: _, ...rest } = prev
          return rest
        })
      }

      // Only auto-save if not in card actions mode
      if (!isCardActions) {
        onSave(prepareGroupForSave(newEditableGroup))
      }
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Save the current editable group
    updateFloatInputs(() => ({}))
    const prepared = prepareGroupForSave(editableGroup)

    onSave(prepared)
    setIsEditMode(false)
  }

  const handleCancel = () => {
    // Reset to initial group
    setEditableGroup(JSON.parse(JSON.stringify(initialGroup)))
    setIsEditMode(false)
    updateFloatInputs(() => ({}))
  }

  //log floatInputs

  return (
    <CollapsibleCard
      open={true}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {editableGroup.name} <HaufeIcon />
        </Box>
      }
      cardActions={
        cardActions ||
        (isCardActions ? (
          <CardActionButton
            isEdit={isEditMode}
            setIsEdit={setIsEditMode}
            handleCancel={handleCancel}
            form="formNote"
          />
        ) : null)
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 1,
        }}
        component="form"
        onSubmit={handleSubmit}
        id="formNote"
      >
        {editableGroup.deviceData &&
          editableGroup.deviceData.map((item, itemIdx) =>
            item.isArray ? (
              <Box key={item.id || `array-item-${itemIdx}`} sx={{ mb: 2, width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    padding: 1,
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">{item.name}</Typography>
                  {(!isCardActions || isEditMode) && (
                    <Button
                      size="small"
                      onClick={() => addArrayItem(itemIdx)}
                      startIcon={<AddCircleOutline fontSize="small" />}
                      variant="contained"
                    >
                      Wert hinzufügen
                    </Button>
                  )}
                </Box>
                {isSaveDisabled && item.isOperatingMode && (
                  <Typography variant="subtitle2" sx={{ backgroundColor: "red", p: 1 }}>
                    Betriebsmodi sind erforderlich. Bitte fügen Sie mindestens einen Betriebsmodus
                    hinzu.
                  </Typography>
                )}
                {item.data &&
                  item.data.map((dataEntry, dataIdx) => (
                    <Box
                      key={`${item.id || item.name}-data-${dataIdx}`}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      {item.dataType === DataType.BOOLEAN ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={dataEntry.value as boolean}
                              onChange={(e) =>
                                handleInputChange(itemIdx, e.target.checked, "value", dataIdx)
                              }
                              disabled={isCardActions && !isEditMode}
                            />
                          }
                          label={item.name}
                        />
                      ) : (
                        <TextField
                          size="small"
                          value={
                            item.dataType === DataType.FLOAT
                              ? getFloatDisplayValue(item, dataEntry.value, dataIdx)
                              : dataEntry.value
                          }
                          name={`${item.name}-${dataIdx}-value`}
                          fullWidth
                          type="text"
                          onChange={(e) =>
                            handleInputChange(itemIdx, e.target.value, "value", dataIdx)
                          }
                          disabled={isCardActions && !isEditMode}
                          onBlur={() => {
                            if (item.dataType === DataType.FLOAT) {
                              const key = getFloatFieldKey(item, dataIdx)
                              updateFloatInputs((prev) => {
                                const { [key]: _, ...rest } = prev
                                return rest
                              })
                            }
                          }}
                          slotProps={{
                            htmlInput: {
                              ...(item.dataType === DataType.FLOAT && {
                                inputMode: "decimal",
                                pattern: "-?[0-9]*([,.][0-9]*)?",
                              }),
                            },
                          }}
                        />
                      )}
                      {item.unit.length > 0 && (
                        <FormControl sx={{ ml: 1, minWidth: 100 }} size="small">
                          <Select
                            value={dataEntry.unit || (item.unit.length > 0 ? item.unit[0] : "")}
                            name={`${item.name}-${dataIdx}-unit`}
                            onChange={(e) =>
                              handleInputChange(itemIdx, e.target.value, "unit", dataIdx)
                            }
                            disabled={isCardActions && !isEditMode}
                          >
                            {item.unit.map((unitValue) => (
                              <MenuItem key={unitValue} value={unitValue}>
                                {unitValue}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {(!isCardActions || isEditMode) && (
                        <IconButton
                          onClick={() => removeArrayItem(itemIdx, dataIdx)}
                          size="small"
                          sx={{ ml: 0.5 }}
                        >
                          <RemoveCircleOutline />
                        </IconButton>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : (
              <Box
                key={item.id || `single-item-${itemIdx}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1,
                  width: "100%",
                }}
              >
                {item.dataType === DataType.BOOLEAN ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          item.data && item.data.length > 0
                            ? (item.data[0].value as boolean)
                            : false
                        }
                        onChange={(e) => handleInputChange(itemIdx, e.target.checked, "value")}
                        disabled={isCardActions && !isEditMode}
                      />
                    }
                    label={item.name}
                  />
                ) : (
                  <TextField
                    size="small"
                    value={
                      item.data && item.data.length > 0
                        ? item.dataType === DataType.FLOAT
                          ? getFloatDisplayValue(item, item.data[0].value)
                          : item.data[0].value
                        : ""
                    }
                    name={`${item.name}-value`}
                    fullWidth
                    label={item.name}
                    type="text"
                    onChange={(e) => handleInputChange(itemIdx, e.target.value, "value")}
                    disabled={isCardActions && !isEditMode}
                    onBlur={() => {
                      if (item.dataType === DataType.FLOAT) {
                        const key = getFloatFieldKey(item)
                        updateFloatInputs((prev) => {
                          const { [key]: _, ...rest } = prev
                          return rest
                        })
                      }
                    }}
                    inputProps={{
                      ...(item.dataType === DataType.FLOAT && {
                        inputMode: "decimal",
                        pattern: "-?[0-9]*([,.][0-9]*)?",
                      }),
                    }}
                  />
                )}
                {item.unit.length > 0 && (
                  <FormControl sx={{ ml: 1, minWidth: 100 }} size="small">
                    <Select
                      value={
                        item.data && item.data.length > 0 && item.data[0].unit
                          ? item.data[0].unit
                          : item.unit.length > 0
                            ? item.unit[0]
                            : ""
                      }
                      name={`${item.name}-unit`}
                      onChange={(e) => handleInputChange(itemIdx, e.target.value, "unit")}
                      disabled={isCardActions && !isEditMode}
                    >
                      {item.unit.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {item.unit.length > 0 && <Box sx={{ width: 40, ml: 0.5 }} />}
              </Box>
            )
          )}
      </Box>
    </CollapsibleCard>
  )
}
